import { useState, useEffect, useCallback } from 'react';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import { Plus, Trash2, Edit2, Car, History, Lock, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { safeFormatDate } from '../lib/dateTime';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { GoBDDeleteConfirmDialog, useGoBDDelete } from '../components/GoBDDeleteConfirmDialog';
import { AuditHistoryPanel } from '../components/AuditHistoryPanel';
import { LockBadge, StornoBadge } from '../components/LockBadge';
import { UnlockRequestDialog } from '../components/UnlockRequestDialog';
import { CorrectionDialog } from '../components/CorrectionDialog';

export default function MileagePage() {
  const { api, user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  // GoBD Delete Dialog hook
  const { 
    dialogOpen: deleteDialogOpen, 
    setDialogOpen: setDeleteDialogOpen, 
    deleteConfig, 
    requestDelete, 
    handleConfirm: handleDeleteConfirm, 
    handleCancel: handleDeleteCancel 
  } = useGoBDDelete();
  
  // Audit History state
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [historyEntry, setHistoryEntry] = useState({ collection: '', id: '' });

  // Lock status state
  const [lockStatuses, setLockStatuses] = useState({});
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockEntries, setUnlockEntries] = useState([]);
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [correctionEntry, setCorrectionEntry] = useState(null);
  
  const showHistory = (collection, id) => {
    setHistoryEntry({ collection, id });
    setHistoryPanelOpen(true);
  };
  
  // Date range filter
  const today = new Date();
  const [dateRange, setDateRange] = useState({
    start: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    end: format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
  });
  
  const [formData, setFormData] = useState({
    date: format(today, 'yyyy-MM-dd'),
    customer_id: '',
    project_id: '',
    kilometers: '',
    description: '',
  });

  const fetchData = useCallback(async () => {
    if (!api) return;
    try {
      setLoading(true);
      const [entriesRes, customersRes, projectsRes] = await Promise.all([
        api.get(`/mileage?start_date=${dateRange.start}&end_date=${dateRange.end}`),
        api.get('/customers'),
        api.get('/projects'),
      ]);
      setEntries(entriesRes.data);
      setCustomers(customersRes.data);
      setProjects(projectsRes.data.filter(p => p.is_active));
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [api, dateRange]);

  // Fetch lock statuses for mileage entries
  const fetchLockStatuses = useCallback(async (mileageEntries) => {
    const ids = mileageEntries.map(e => e.id);
    if (ids.length === 0) return;
    
    try {
      const res = await api.post('/locks/status/bulk', {
        collection_name: 'mileage_entries',
        document_ids: ids,
      });
      const results = {};
      Object.entries(res.data).forEach(([id, status]) => {
        results[id] = status;
      });
      setLockStatuses(results);
    } catch (error) {
      console.error('Failed to fetch lock statuses:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch lock statuses when entries change
  useEffect(() => {
    if (entries.length > 0) {
      fetchLockStatuses(entries);
    }
  }, [entries, fetchLockStatuses]);

  // Helper to get lock status for an entry
  const getLockStatus = (entryId) => {
    return lockStatuses[entryId] || { level: 'open' };
  };

  // Check if entry can be edited (based on lock level and unlock request)
  const canEditEntry = (entry) => {
    const lockInfo = getLockStatus(entry.id);
    if (lockInfo.level === 'open' || lockInfo.level === 'soft') return true;
    if (lockInfo.level === 'hard' && lockInfo.unlock_request?.status === 'approved') return true;
    return false;
  };

  // Open unlock request dialog
  const openUnlockDialog = (entriesArray) => {
    setUnlockEntries(entriesArray);
    setUnlockDialogOpen(true);
  };

  // Open correction/storno dialog
  const openCorrectionDialog = (entry) => {
    setCorrectionEntry(entry);
    setCorrectionDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      date: format(today, 'yyyy-MM-dd'),
      customer_id: '',
      project_id: '',
      kilometers: '',
      description: '',
    });
    setEditingEntry(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      customer_id: entry.customer_id,
      project_id: entry.project_id || '',
      kilometers: entry.kilometers.toString(),
      description: entry.description || '',
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error('Bitte wählen Sie einen Kunden aus');
      return;
    }
    
    if (!formData.kilometers || parseFloat(formData.kilometers) <= 0) {
      toast.error('Bitte geben Sie eine gültige Kilometeranzahl ein');
      return;
    }

    const data = {
      ...formData,
      kilometers: parseFloat(formData.kilometers),
      project_id: formData.project_id === 'none' ? null : formData.project_id || null,
    };

    try {
      if (editingEntry) {
        await api.put(`/mileage/${editingEntry.id}`, data);
        toast.success('Eintrag aktualisiert');
      } else {
        await api.post('/mileage', data);
        toast.success('Eintrag erstellt');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    
    try {
      await api.delete(`/mileage/${entryId}`);
      toast.success('Eintrag gelöscht');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const filteredProjects = projects.filter(
    p => !formData.customer_id || p.customer_id === formData.customer_id
  );

  const totalKilometers = entries.reduce((sum, e) => sum + e.kilometers, 0);

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Car className="w-6 h-6" />
            Kilometer / Fahrtkosten
          </h1>
          <p className="text-muted-foreground mt-1">Erfassen Sie Ihre Fahrten</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-cyan-600 hover:bg-cyan-700">
          <Plus className="w-4 h-4 mr-2" />
          Neue Fahrt
        </Button>
      </div>

      {/* Date Filter */}
      <Card className="bg-card/50 border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="space-y-2 flex-1">
              <Label className="text-muted-foreground">Von</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="bg-input border-border text-foreground"
              />
            </div>
            <div className="space-y-2 flex-1">
              <Label className="text-muted-foreground">Bis</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="bg-input border-border text-foreground"
              />
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Gesamt</p>
              <p className="text-xl font-bold text-cyan-400">{totalKilometers.toFixed(1)} km</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entries List */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Laden...</div>
      ) : entries.length === 0 ? (
        <Card className="bg-card/50 border-border">
          <CardContent className="p-8 text-center">
            <Car className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Keine Fahrten im gewählten Zeitraum</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border text-left text-sm text-muted-foreground">
                  <th className="p-3">Datum</th>
                  <th className="p-3">Kunde</th>
                  <th className="p-3">Projekt</th>
                  <th className="p-3">Kilometer</th>
                  <th className="p-3">Beschreibung</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const lockInfo = getLockStatus(entry.id);
                  const entryEditable = canEditEntry(entry);
                  const isStorno = entry.is_correction;
                  const isLocked = lockInfo.level === 'hard' || lockInfo.level === 'final';
                  
                  return (
                  <tr key={entry.id} className={`border-b border-border hover:bg-accent ${isStorno ? 'opacity-60 line-through' : ''}`}>
                    <td className="p-3 text-foreground">
                      <div className="flex items-center gap-2">
                        {safeFormatDate(entry.date, 'dd.MM.yyyy')}
                        <LockBadge lockInfo={lockInfo} size="sm" />
                        {isStorno && <StornoBadge entry={entry} />}
                      </div>
                    </td>
                    <td className="p-3 text-foreground">{entry.customer_name}</td>
                    <td className="p-3 text-muted-foreground">{entry.project_name || '-'}</td>
                    <td className="p-3 text-cyan-400 font-medium">{entry.kilometers} km</td>
                    <td className="p-3 text-muted-foreground max-w-xs truncate">{entry.description || '-'}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        entry.billing_status === 'billed' 
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {entry.billing_status === 'billed' ? 'Abgerechnet' : 'Offen'}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-2">
                        {entryEditable && entry.billing_status !== 'billed' ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(entry)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(entry.id)}
                              className="text-red-600 dark:text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            {lockInfo.level === 'hard' && lockInfo.can_request_unlock && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openUnlockDialog([entry])}
                                className="text-amber-600 dark:text-amber-400 hover:text-amber-300"
                                title="Entsperrung beantragen"
                              >
                                <Lock className="w-4 h-4" />
                              </Button>
                            )}
                            {lockInfo.level === 'final' && (user?.role === 'admin' || user?.role === 'accounting') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openCorrectionDialog(entry)}
                                className="text-red-600 dark:text-red-400 hover:text-red-300"
                                title="Storno erstellen"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => showHistory('mileage_entries', entry.id)}
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-300"
                          title="Änderungshistorie"
                        >
                          <History className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {entries.map((entry) => {
              const lockInfo = getLockStatus(entry.id);
              const entryEditable = canEditEntry(entry);
              const isStorno = entry.is_correction;
              
              return (
              <Card key={entry.id} className={`bg-card/50 border-border ${isStorno ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className={`text-foreground font-medium ${isStorno ? 'line-through' : ''}`}>
                          {safeFormatDate(entry.date, 'dd.MM.yyyy')}
                        </p>
                        <LockBadge lockInfo={lockInfo} size="sm" />
                        {isStorno && <StornoBadge entry={entry} />}
                      </div>
                      <p className="text-muted-foreground text-sm">{entry.customer_name}</p>
                    </div>
                    <p className="text-cyan-400 font-bold text-lg">{entry.kilometers} km</p>
                  </div>
                  {entry.project_name && (
                    <p className="text-muted-foreground text-sm mb-2">Projekt: {entry.project_name}</p>
                  )}
                  {entry.description && (
                    <p className="text-muted-foreground text-sm mb-3">{entry.description}</p>
                  )}
                  <div className="flex justify-between items-center">
                    <span className={`px-2 py-1 rounded text-xs ${
                      entry.billing_status === 'billed' 
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {entry.billing_status === 'billed' ? 'Abgerechnet' : 'Offen'}
                    </span>
                    <div className="flex gap-2">
                      {entryEditable && entry.billing_status !== 'billed' ? (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(entry)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} className="text-red-600 dark:text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          {lockInfo.level === 'hard' && lockInfo.can_request_unlock && (
                            <Button variant="ghost" size="sm" onClick={() => openUnlockDialog([entry])} className="text-amber-600 dark:text-amber-400">
                              <Lock className="w-4 h-4" />
                            </Button>
                          )}
                          {lockInfo.level === 'final' && (user?.role === 'admin' || user?.role === 'accounting') && (
                            <Button variant="ghost" size="sm" onClick={() => openCorrectionDialog(entry)} className="text-red-600 dark:text-red-400">
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => showHistory('mileage_entries', entry.id)}
                        className="text-indigo-600 dark:text-indigo-400"
                      >
                        <History className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Fahrt bearbeiten' : 'Neue Fahrt erfassen'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Datum</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="bg-input border-border text-foreground"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Kunde *</Label>
              <Select
                value={formData.customer_id}
                onValueChange={(value) => setFormData({ ...formData, customer_id: value, project_id: '' })}
              >
                <SelectTrigger className="bg-input border-border text-foreground">
                  <SelectValue placeholder="Kunde auswählen..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Auftrag (optional)</Label>
              <Select
                value={formData.project_id}
                onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                disabled={!formData.customer_id}
              >
                <SelectTrigger className="bg-input border-border text-foreground">
                  <SelectValue placeholder="Auftrag auswählen..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none">Kein Projekt</SelectItem>
                  {filteredProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Kilometer *</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={formData.kilometers}
                onChange={(e) => setFormData({ ...formData, kilometers: e.target.value })}
                placeholder="z.B. 45.5"
                className="bg-input border-border text-foreground"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Beschreibung</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="z.B. Kundenbesuch Hamburg"
                className="bg-input border-border text-foreground"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                {editingEntry ? 'Aktualisieren' : 'Speichern'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* GoBD Delete Confirmation Dialog */}
      <GoBDDeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={deleteConfig.title}
        itemDescription={deleteConfig.itemDescription}
        requireReason={deleteConfig.requireReason}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Audit History Panel Dialog */}
      <Dialog open={historyPanelOpen} onOpenChange={setHistoryPanelOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Änderungshistorie
            </DialogTitle>
          </DialogHeader>
          {historyEntry.id && (
            <AuditHistoryPanel
              collectionName={historyEntry.collection}
              documentId={historyEntry.id}
              isOpen={true}
              compact={false}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Unlock Request Dialog */}
      <UnlockRequestDialog
        open={unlockDialogOpen}
        onOpenChange={setUnlockDialogOpen}
        entries={unlockEntries}
        collectionName="mileage_entries"
        onSuccess={() => {
          fetchData();
        }}
      />

      {/* Correction/Storno Dialog */}
      <CorrectionDialog
        open={correctionDialogOpen}
        onOpenChange={setCorrectionDialogOpen}
        entry={correctionEntry}
        collectionName="mileage_entries"
        onSuccess={() => {
          fetchData();
        }}
      />
    </div>
  );
}
